// Rejected Lane Recognition — the research-memory layer (pure logic, no deps).
//
// Seeds the corpus with the SEVEN failed lanes from our own track record, and a
// CONSERVATIVE matcher that optimizes for trust, not coverage:
//   • a wrong match is worse than no match;
//   • only High/Medium confidence is ever SURFACED (see shouldSurface);
//   • a strong (distinctive) trigger is REQUIRED for any surfaced match —
//     generic words alone never trigger a warning.
// See docs/track-record/HYPOTHESIS-INTAKE-UX-SPEC.md → "Conservative Matching".

import type { RejectedLane, RejectedLaneMatch, MatchConfidence } from '../../types/validation';

// The seven seeded lanes. `triggers` are supporting context; `strongTriggers`
// are distinctive phrases that must appear for a match to be surfaced.
export const REJECTED_LANES: RejectedLane[] = [
  {
    id: 'fast_crypto_timing',
    name: 'Fast single-venue crypto price-timing',
    failureAxis: 'cost',
    memory: 'the edge did not beat random trading once real fees were counted',
    triggers: ['crypto', 'bitcoin', 'btc', 'eth', 'altcoin', 'breakout', 'donchian', 'price', 'candle'],
    // NB: triggers within a list must not be prefixes of each other, or one
    // word double-counts (e.g. "scalp" ⊂ "scalping"). Keep the shortest form.
    strongTriggers: ['scalp', '1 minute', '1-minute', '1m ', '5m ', 'high frequency', 'hft', 'tick chart'],
    breakSuggestions: [
      'Use a slower horizon (hours/days, not minutes)',
      'Assume maker / limit fills instead of taker fees',
      'Reduce turnover so fees are a smaller share of the edge',
    ],
  },
  {
    id: 'liquidation_fade',
    name: 'Crypto liquidation / order-flow fade',
    failureAxis: 'cost',
    memory: 'the reversal was real but smaller than trading fees at retail taker rates',
    triggers: ['reversal', 'reverse', 'spike', 'wick', 'order flow', 'orderflow', 'cascade', 'squeeze', 'bounce'],
    // 'liquidation' already matches 'liquidations'/'liquidation cascade' as a
    // substring — listing those too would double-count one word into a match.
    strongTriggers: ['liquidation', 'long squeeze', 'short squeeze'],
    breakSuggestions: [
      'Attack the cost axis: maker execution or a slower horizon',
      'Trade only the largest cascades where the move exceeds fees',
      'Change the venue / fee-tier assumptions',
    ],
  },
  {
    id: 'funding_directional',
    name: 'Funding / open-interest as a directional signal',
    failureAxis: 'dead_end',
    memory: 'no robust directional edge survived matched-random controls (0 of 20)',
    // Keep 'open interest' / 'perp' only in ONE list each so a single phrase
    // can't corroborate itself across lists ('perp' ⊂ 'perpetual').
    triggers: ['funding', 'oi ', 'perp', 'positioning', 'leverage'],
    strongTriggers: ['funding rate', 'open interest', 'basis trade', 'perpetual funding'],
    breakSuggestions: [
      'Test funding as a CARRY / cashflow capture, not a price-direction predictor',
      'Combine it with a separate, independent signal',
      'Change the holding horizon materially',
    ],
  },
  {
    id: 'pead_earnings',
    name: 'Earnings-drift (PEAD) as an earnings-specific edge',
    failureAxis: 'placebo',
    memory: 'the drift turned out to be generic momentum, not earnings-specific, under placebo controls',
    triggers: ['earnings', 'announcement', 'drift', 'eps', 'guidance', 'report'],
    strongTriggers: ['pead', 'post-earnings', 'earnings surprise', 'earnings drift'],
    breakSuggestions: [
      'Use a true consensus surprise (SUE), not the price reaction',
      'Control for plain momentum explicitly',
      'Separate the earnings signal from the trend it rides on',
    ],
  },
  {
    id: 'equity_momentum',
    name: 'Slow single-asset equity momentum',
    failureAxis: 'power',
    memory: 'a single short book is structurally underpowered — blind, not negative',
    triggers: ['momentum', 'trend', 'relative strength', '12-month', '12 month'],
    strongTriggers: ['time series momentum', 'tsmom', '12-1', 'single stock momentum'],
    breakSuggestions: [
      'Add breadth: many instruments or asset classes',
      'Use a much longer history',
      'Diversify into a cross-asset book to lift statistical power',
    ],
  },
  {
    id: 'crossasset_trend',
    name: 'Cross-asset trend on free futures data',
    failureAxis: 'proxy',
    memory: 'roll-artifact free futures + too few years — underpowered and proxy-limited',
    triggers: ['trend', 'futures', 'cross-asset', 'cross asset', 'roll', 'diversified'],
    strongTriggers: ['managed futures', 'trend following', 'cta', 'continuous futures'],
    breakSuggestions: [
      'Use properly back-adjusted continuous futures',
      'Extend the history to several decades',
      'Add crypto / FX for additional breadth',
    ],
  },
  {
    id: 'token_unlock',
    name: 'Token-unlock event study',
    failureAxis: 'data',
    memory: 'free historical unlock data is paywalled/unavailable — the study could not be run',
    triggers: ['unlock', 'vesting', 'emission', 'token release', 'cliff', 'supply'],
    strongTriggers: ['token unlock', 'vesting schedule', 'emission schedule', 'unlock schedule'],
    breakSuggestions: [
      'Secure a licensed historical unlock dataset',
      'Use an on-chain proxy you can actually source',
      'Reframe around data you already have',
    ],
  },
];

function countHits(haystack: string, needles: string[]): number {
  let n = 0;
  for (const t of needles) if (haystack.includes(t)) n++;
  return n;
}

// Score an idea against every lane; return the best match with a confidence,
// or null when nothing meaningfully resembles a known lane (a FRESH lane).
//
// Conservative — a single strong keyword alone NEVER surfaces a match (a wrong
// match is worse than no match). Surfacing requires either two distinctive
// (strong) triggers, or one strong trigger PLUS corroborating context:
//   high   : ≥2 strong triggers, OR (≥1 strong AND ≥2 weak context hits)
//   medium : ≥1 strong AND ≥1 weak context hit
//   low    : a strong keyword alone, or weak-only resemblance — held internally,
//            NOT surfaced (shouldSurface returns false)
//   (none) : no resemblance → returns null
export function matchRejectedLane(ideaText: string): RejectedLaneMatch | null {
  const text = ' ' + (ideaText || '').toLowerCase() + ' ';

  let best: RejectedLane | null = null;
  let bestScore = -1;
  let bestStrong = 0;
  for (const lane of REJECTED_LANES) {
    const strong = countHits(text, lane.strongTriggers);
    const weak = countHits(text, lane.triggers);
    const score = strong * 2 + weak;
    if (score > bestScore || (score === bestScore && strong > bestStrong)) {
      best = lane;
      bestScore = score;
      bestStrong = strong;
    }
  }
  if (!best) return null;

  const strong = countHits(text, best.strongTriggers);
  const weak = countHits(text, best.triggers);

  let confidence: MatchConfidence;
  if (strong >= 2 || (strong >= 1 && weak >= 2)) confidence = 'high';
  else if (strong >= 1 && weak >= 1) confidence = 'medium';
  else if (strong >= 1 || weak >= 2) confidence = 'low';
  else confidence = 'none';

  if (confidence === 'none') return null;

  return {
    laneId: best.id,
    laneName: best.name,
    failureAxis: best.failureAxis,
    memory: best.memory,
    confidence,
    breakSuggestions: best.breakSuggestions,
  };
}

// Conservative-matching gate: ONLY High/Medium matches are ever shown to the
// user. Low-confidence matches are kept internally (e.g. to gently probe) but
// never rendered as a warning. A wrong match is worse than no match.
export function shouldSurface(match: RejectedLaneMatch | null): boolean {
  return !!match && (match.confidence === 'high' || match.confidence === 'medium');
}

// Human label for a failure axis (used in copy).
export function failureAxisLabel(axis: RejectedLane['failureAxis']): string {
  switch (axis) {
    case 'cost': return 'cost';
    case 'power': return 'statistical power';
    case 'placebo': return 'specificity (placebo)';
    case 'proxy': return 'data proxy';
    case 'data': return 'data availability';
    case 'dead_end': return 'no directional edge';
    default: return axis;
  }
}
